import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Image as ImageIcon, Lock, Trash2, Upload } from 'lucide-react';
import { dateTimeLabel } from '@adisys/shared';
import { api } from '../../../lib/api';
import { useAuth } from '../../../lib/auth';
import { Notice, Section, errorText, useOpenFile, type TabProps } from '../../../lib/crm';
import {
  Button, EmptyState, ErrorState, Field, Input, Modal, Select, Skeleton, StatusBadge, useToast,
} from '../../../components/ui';

const CATEGORY_LABEL: Record<string, string> = {
  site_photo: 'Site photo', survey_report: 'Survey report', datasheet: 'Datasheet', product_image: 'Product image',
  customer_po: 'Customer PO', approval_proof: 'Approval proof', challan: 'Delivery challan', invoice: 'Invoice',
  contract: 'Contract', architecture: 'System architecture', other: 'Other',
};
const UPLOAD_CATEGORIES = ['site_photo', 'survey_report', 'customer_po', 'approval_proof', 'challan', 'invoice', 'contract', 'architecture', 'other'];
const SOURCE_LABEL: Record<string, string> = {
  project: 'Project', survey: 'Survey', quotation: 'Quotation', order: 'Order', challan: 'Delivery', invoice: 'Invoice', lead: 'Enquiry',
};
const PROTECTED = ['customer_po', 'approval_proof'];

const bytes = (n: number) => (n > 1_048_576 ? `${(n / 1_048_576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

export function DocumentsTab({ project }: TabProps) {
  const { user, can } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const open = useOpenFile();
  const fileRef = useRef<HTMLInputElement>(null);
  const [category, setCategory] = useState('other');
  const [title, setTitle] = useState('');
  const [pending, setPending] = useState<FileList | null>(null);
  const [filterCat, setFilterCat] = useState('');
  const [filterSrc, setFilterSrc] = useState('');
  const [toDelete, setToDelete] = useState<any>(null);

  const key = ['crm', 'project', project.id, 'documents'];
  const q = useQuery({ queryKey: key, queryFn: () => api.get(`/crm/projects/${project.id}/documents`) });

  const canUpload = can('crm.survey.manage', 'crm.boq.manage', 'crm.quote.manage', 'crm.approval.record',
    'crm.procurement.manage', 'crm.finance.manage', 'crm.execution.update', 'crm.stage.override');
  const writable = project.status === 'active' && canUpload;

  const upload = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      Array.from(pending ?? []).forEach((f) => fd.append('files', f));
      fd.append('entityType', 'project'); fd.append('entityId', project.id); fd.append('category', category);
      if (title.trim()) fd.append('title', title.trim());
      return api.upload('/crm/documents', fd);
    },
    onSuccess: () => { toast.success('Uploaded'); setPending(null); setTitle(''); void qc.invalidateQueries({ queryKey: key }); },
    onError: (e) => toast.error('Upload failed', errorText(e)),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/crm/documents/${id}`),
    onSuccess: () => { toast.success('Document removed'); setToDelete(null); void qc.invalidateQueries({ queryKey: key }); },
    onError: (e) => toast.error('Could not remove it', errorText(e)),
  });

  const all: any[] = q.data?.data ?? [];
  const rows = all.filter((d) => (!filterCat || d.category === filterCat) && (!filterSrc || d.entityType === filterSrc));
  const cats = [...new Set(all.map((d) => d.category))];
  const srcs = [...new Set(all.map((d) => d.entityType))];

  return (
    <Section title="Documents" description="Everything attached to this project: survey photos, approvals, challans and invoices.">
      {canUpload && !writable && <Notice tone="warning">This project is {project.status.replace('_', ' ')}, so files cannot be added.</Notice>}
      {!canUpload && <Notice>You can open documents here. Uploading needs a role that works on this project.</Notice>}

      {writable && (
        <div className="flex flex-wrap items-end gap-2 rounded-lg bg-sunken/60 p-3">
          <Field label="Type" className="w-44">
            <Select value={category} onChange={(e) => setCategory(e.target.value)}>
              {UPLOAD_CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY_LABEL[c]}</option>)}
            </Select>
          </Field>
          <Field label="Title (optional)" className="min-w-[180px] flex-1">
            <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={160} placeholder="Defaults to the file name" />
          </Field>
          <input ref={fileRef} type="file" multiple accept="image/*,application/pdf" className="hidden"
            onChange={(e) => setPending(e.target.files)} />
          <Button icon={<Upload className="h-4 w-4" />} onClick={() => fileRef.current?.click()}>
            {pending?.length ? `${pending.length} file${pending.length === 1 ? '' : 's'} chosen` : 'Choose files'}
          </Button>
          <Button variant="primary" disabled={!pending?.length} loading={upload.isPending} onClick={() => upload.mutate()}>Upload</Button>
        </div>
      )}

      {all.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <Select value={filterCat} onChange={(e) => setFilterCat(e.target.value)} className="w-48" aria-label="Filter by type">
            <option value="">All types</option>
            {cats.map((c) => <option key={c} value={c}>{CATEGORY_LABEL[c] ?? c}</option>)}
          </Select>
          <Select value={filterSrc} onChange={(e) => setFilterSrc(e.target.value)} className="w-48" aria-label="Filter by source">
            <option value="">All sources</option>
            {srcs.map((s) => <option key={s} value={s}>{SOURCE_LABEL[s] ?? s}</option>)}
          </Select>
        </div>
      )}

      {q.isLoading ? <Skeleton className="h-32" />
        : q.isError ? <ErrorState error={q.error} onRetry={() => q.refetch()} />
        : rows.length === 0 ? <EmptyState icon={<FileText className="h-5 w-5" />} title={all.length ? 'No documents match' : 'No documents yet'} />
        : (
          <ul className="divide-y divide-line rounded-lg ring-1 ring-line">
            {rows.map((d) => {
              const locked = PROTECTED.includes(d.category) && !can('crm.stage.override');
              const mine = d.uploadedBy === user?.id || can('crm.stage.override');
              return (
                <li key={d.id} className="flex items-center gap-3 px-3.5 py-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-ink-100 text-ink-500">
                    {d.mimeType?.startsWith('image/') ? <ImageIcon className="h-4 w-4" aria-hidden /> : <FileText className="h-4 w-4" aria-hidden />}
                  </span>
                  <button onClick={() => void open(d.fileId)} className="min-w-0 flex-1 text-left">
                    <p className="truncate text-sm font-medium text-ink-900 hover:underline">{d.title ?? 'Untitled'}</p>
                    <p className="truncate text-xs text-ink-500">
                      {d.uploadedByName ?? 'Unknown'} · {dateTimeLabel(d.createdAt)} · {bytes(d.sizeBytes)}
                    </p>
                  </button>
                  <div className="hidden gap-1.5 sm:flex">
                    <StatusBadge dot={false}>{CATEGORY_LABEL[d.category] ?? d.category}</StatusBadge>
                    <StatusBadge tone="info" dot={false}>{SOURCE_LABEL[d.entityType] ?? d.entityType}</StatusBadge>
                  </div>
                  {locked ? (
                    <span className="p-1.5 text-ink-400" title="Approval evidence is part of the record and cannot be removed">
                      <Lock className="h-4 w-4" aria-label="Locked" />
                    </span>
                  ) : mine && writable ? (
                    <button onClick={() => setToDelete(d)} aria-label={`Remove ${d.title ?? 'document'}`}
                      className="rounded-lg p-1.5 text-ink-400 hover:bg-danger-soft hover:text-danger">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  ) : <span className="w-8" />}
                </li>
              );
            })}
          </ul>
        )}

      <Modal open={Boolean(toDelete)} onClose={() => setToDelete(null)} title="Remove this document?" size="sm"
        description={toDelete?.title ?? undefined}
        footer={<>
          <Button onClick={() => setToDelete(null)}>Keep it</Button>
          <Button variant="danger" loading={remove.isPending} onClick={() => remove.mutate(toDelete.id)}>Remove</Button>
        </>}>
        <p className="text-sm text-ink-700">It disappears from the project. The removal is recorded in the audit log.</p>
      </Modal>
    </Section>
  );
}
